import * as React from 'react';

import * as styles from './ChatListContextMenu.css';
import { useContextMenu } from '../../hooks/useContextMenu';

export type TMenuItem = {
  title: string;
  handler(chatId: string): void;
};

type TChatListContextMenuProps = {
  listRef: React.RefObject<HTMLDivElement>;
  menuItems: TMenuItem[];
  onShowContextMenu(chatId: string): void;
};

export const ChatListContextMenu: React.VFC<TChatListContextMenuProps> = ({
  listRef,
  menuItems,
  onShowContextMenu,
}) => {
  const [currentChatId, setCurrentChatId] = React.useState('');

  const onShowContextMenuHandler = React.useCallback(
    (event: MouseEvent): boolean => {
      const chatId = ((event.target as HTMLElement).closest('[data-chatid]') as HTMLElement)?.dataset?.chatid as string;

      if (!chatId) {
        return false;
      }

      setCurrentChatId(chatId);
      onShowContextMenu(chatId);

      return true;
    },
    [onShowContextMenu],
  );

  const onHideContextMenu = React.useCallback(() => {
    setCurrentChatId('');
  }, []);

  const { anchorPoint, show, closeContextMenu } = useContextMenu({
    elementRef: listRef,
    onShowContextMenu: onShowContextMenuHandler,
    onHideContextMenu,
    isEnabled: true,
  });

  React.useEffect(() => {
    document.addEventListener('click', closeContextMenu);

    return () => {
      document.removeEventListener('click', closeContextMenu);
    };
  }, [closeContextMenu]);

  const items = React.useMemo(
    () =>
      menuItems.map(({ title, handler }, index) => (
        <li
          key={index}
          className={styles['item']}
          onClick={() => {
            handler(currentChatId);
          }}
          data-name="ChatListContextMenuItem"
        >
          {title}
        </li>
      )),
    [menuItems, currentChatId],
  );

  if (!show) {
    return null;
  }

  return (
    <ul className={styles['menu']} style={{ top: anchorPoint.y, left: anchorPoint.x }}>
      {items}
    </ul>
  );
};
